'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, MapPin, Briefcase, Truck, Settings, BarChart3, ChevronLeft, ChevronRight, Zap, Menu, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
  badgeColor?: string;
}

const navGroups = [
  {
    label: 'Operations',
    items: [
      { id: 'nav-dashboard', label: 'Fleet Dashboard', icon: LayoutDashboard, href: '/fleet-dashboard' },
      { id: 'nav-tracking', label: 'Live Tracking', icon: MapPin, href: '/live-tracking', badge: 3, badgeColor: 'bg-red-500' },
      { id: 'nav-jobs', label: 'Job Management', icon: Briefcase, href: '/job-management', badge: 7, badgeColor: 'bg-amber-500' },
    ],
  },
  {
    label: 'Assets',
    items: [
      { id: 'nav-assets', label: 'Asset Management', icon: Truck, href: '/asset-management' },
      { id: 'nav-reports', label: 'Reports', icon: BarChart3, href: '/reports' },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'nav-integrations', label: 'Integrations', icon: Zap, href: '/integration-settings' },
      { id: 'nav-settings', label: 'Settings', icon: Settings, href: '/integration-settings' },
    ],
  },
];

interface SidebarProps {
  currentPath: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const NavContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <>
      {/* Logo */}
      <div className={`flex items-center h-14 border-b border-zinc-800 px-3 flex-shrink-0 ${collapsed && !isMobile ? 'justify-center' : 'gap-2'}`}>
        <AppLogo size={28} />
        {(!collapsed || isMobile) && (
          <span className="font-semibold text-base text-zinc-100 tracking-tight flex-1">FleetJockey</span>
        )}
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-all"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-4">
        {navGroups.map((group) => (
          <div key={`group-${group.label}`}>
            {(!collapsed || isMobile) && (
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 px-2 mb-1">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = currentPath === item.href;
                return (
                  <li key={item.id}>
                    <Link
                      href={item.href}
                      onClick={() => isMobile && setMobileOpen(false)}
                      className={`
                        group relative flex items-center gap-2.5 px-2 py-2 rounded-md text-sm font-medium transition-all duration-150
                        ${isActive ? 'bg-amber-500/15 text-amber-400' : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100'}
                        ${collapsed && !isMobile ? 'justify-center' : ''}
                      `}
                    >
                      <Icon size={16} className="flex-shrink-0" />
                      {(!collapsed || isMobile) && <span className="flex-1">{item.label}</span>}
                      {(!collapsed || isMobile) && item.badge && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white ${item.badgeColor}`}>
                          {item.badge}
                        </span>
                      )}
                      {collapsed && !isMobile && item.badge && (
                        <span className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 text-[8px] font-bold flex items-center justify-center rounded-full text-white ${item.badgeColor}`}>
                          {item.badge}
                        </span>
                      )}
                      {collapsed && !isMobile && (
                        <div className="absolute left-full ml-2 px-2 py-1 bg-zinc-800 border border-zinc-700 text-zinc-100 text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150">
                          {item.label}
                        </div>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom: collapse toggle + user */}
      <div className="border-t border-zinc-800 p-2 space-y-1 flex-shrink-0">
        <div className={`flex items-center gap-2 px-2 py-2 rounded-md ${collapsed && !isMobile ? 'justify-center' : ''}`}>
          {(!collapsed || isMobile) && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-zinc-200 truncate">Dispatch Center</p>
              <p className="text-[10px] text-zinc-500 truncate">dispatch@fleetjockey.io</p>
            </div>
          )}
          <div className="w-7 h-7 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-bold text-amber-400">DC</span>
          </div>
        </div>
        {!isMobile && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 rounded-md text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 transition-all duration-150"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            {!collapsed && <span className="text-xs ml-1.5">Collapse</span>}
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger button — shown in Topbar via prop, but we also expose a trigger */}
      {/* Mobile overlay sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-zinc-900 border-r border-zinc-800 flex flex-col z-10">
            <NavContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`
          hidden lg:flex flex-col bg-zinc-900 border-r border-zinc-800 transition-all duration-300 ease-in-out flex-shrink-0
          ${collapsed ? 'w-16' : 'w-60'}
        `}
      >
        <NavContent isMobile={false} />
      </aside>

      {/* Mobile trigger button — rendered as a floating element that Topbar can use */}
      <button
        id="mobile-sidebar-trigger"
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-3.5 left-3 z-40 p-2 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-all"
        aria-label="Open navigation menu"
      >
        <Menu size={18} />
      </button>
    </>
  );
}